import { useEffect, useState } from 'react'
import type { FleetAvailabilityTruck } from '@numeo/types-main-web-app'
import { Button } from '@/components/ds2'
import { fetchTruckLoads, type TruckLoad, type TruckLoadsAnswer } from '../../api/assignDriverApi'
import { AssignDriverAction } from './AssignDriverAction'
import { AssignDriverPanel } from './AssignDriverPanel'
import { clockOf, milesWords, pickupPassedWordsOf } from './assignModel'
import { startAssign, useAssignStore } from './assignStore'

interface ReverseAssignPickerProps {
    tabId: string
    truck: FleetAvailabilityTruck
    onClose: () => void
}

/** The pickup, as the row shows it: "Pickup 14:00", or unknown. */
const pickupWordsOf = (load: TruckLoad): string => {
    const clock = load.pickup_at ? clockOf(load.pickup_at) : ''
    return clock ? `Pickup ${clock}` : 'Pickup time unknown'
}

/**
 * The loads one truck can take (`tms_fleet.availability` in truck mode), in the server's order: nearest deadhead first. Each row shows
 * its pickup, its deadhead and whether the truck is ready in time. Choosing a load assigns it (no dispatch). Each load's answer (a
 * question, a refusal, a failure) sits under its own line; an assigned load offers its Undo there.
 */
export const ReverseAssignPicker = ({ tabId, truck, onClose }: ReverseAssignPickerProps) => {
    const [answer, setAnswer] = useState<TruckLoadsAnswer | undefined>(undefined)
    const [error, setError] = useState(false)
    const assigned = useAssignStore((state) => state.assigned)
    const drivers = truck.drivers.map((driver) => driver.name)

    useEffect(() => {
        let live = true
        fetchTruckLoads(tabId, truck.truck_id)
            .then((next) => {
                if (live) setAnswer(next)
            })
            .catch(() => {
                if (live) setError(true)
            })
        return () => {
            live = false
        }
    }, [tabId, truck.truck_id])

    const loads = answer?.loads
    return (
        <div className="flex flex-col gap-2 rounded-xl bg-default p-3">
            <div className="flex items-center justify-between gap-2">
                <span className="font-medium">{`Assign truck ${truck.truck_number} to a load`}</span>
                <Button size="sm" variant="ghost" onClick={onClose}>
                    Close
                </Button>
            </div>
            {loads === undefined && !error && <p className="py-2 text-foreground-muted">Reading the loads…</p>}
            {error && (
                <p role="alert" className="py-2 text-danger">
                    The loads could not be read.
                </p>
            )}
            {loads !== undefined && loads.length === 0 && <p className="py-2 text-foreground-muted">No load can take this truck.</p>}
            <ul className="flex flex-col">
                {(loads ?? []).map((load) => (
                    <li key={load.load_id} className="flex flex-col gap-1 border-t border-border py-2 first:border-t-0">
                        <div className="flex items-center gap-3">
                            <span className="min-w-0 flex-1">
                                <span className="block truncate font-medium">{`#${load.load_number}`}</span>
                                <span className="block truncate text-foreground-muted">
                                    {load.pickup_passed ? <span className="text-warning">{pickupPassedWordsOf(load.pickup_at)}</span> : pickupWordsOf(load)}
                                    <span className="ml-2">{milesWords(load.deadhead_miles)}</span>
                                    {!load.pickup_passed && load.ready_in_time === true && <span className="ml-2">Ready in time</span>}
                                    {!load.pickup_passed && load.ready_in_time === false && <span className="ml-2 text-danger">Ready after pickup</span>}
                                </span>
                            </span>
                            {assigned[load.load_id] ? (
                                <AssignDriverAction tabId={tabId} loadId={load.load_id} hasTruck picking={false} onOpen={() => undefined} />
                            ) : (
                                <Button
                                    size="sm"
                                    variant="secondary"
                                    aria-label={`Assign truck ${truck.truck_number} to load ${load.load_number}`}
                                    onClick={() =>
                                        void startAssign(tabId, load.load_id, { truckId: truck.truck_id, truckLabel: truck.truck_number, drivers }, false)
                                    }
                                >
                                    Assign
                                </Button>
                            )}
                        </div>
                        <AssignDriverPanel tabId={tabId} loadId={load.load_id} picking={false} onClose={onClose} />
                    </li>
                ))}
            </ul>
            {answer?.truncated && <p className="text-foreground-muted">Some loads were cut short: the list may not be complete.</p>}
        </div>
    )
}
